import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import {  BarChart3, Users, Target } from 'lucide-react';
import axios from "axios";
import { apiUrl, clientheaderconfig } from "../../../Helpers/Config";

interface SpinData {
  prize_text: string;
  prize_id: string;
  count: number;
  percentage: number;
}

const Analytics: React.FC = () => {
  const [spinData, setSpinData] = useState<SpinData[]>([]);
  const [timeWindow, setTimeWindow] = useState('1440');
  const [loading, setLoading] = useState(true);
  const [totalSpins, setTotalSpins] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, [timeWindow]);

  const fetchAnalytics = async () => {
    try {     
      const hoursAgo = new Date();
      hoursAgo.setHours(hoursAgo.getHours() - parseInt(timeWindow));

      // Get spin distribution
      const { data: results } = await axios.get(`${apiUrl}analytics?creditType=ScratchCard&spin_timestamp=${hoursAgo.toISOString()}`, clientheaderconfig);
      const spins = results?.result || [];

      // Calculate distribution
      const distribution: { [key: string]: { count: number; prize_id: string } } = {};
      let customerList = [];
      spins?.forEach(spin => {
        if (!distribution[spin.prize_text]) {
          distribution[spin.prize_text] = { count: 0, prize_id: spin.prize_id };
        }
        distribution[spin.prize_text].count++;
        if(customerList.indexOf(spin.voucherCustomerID)<0) {
          customerList.push(spin.voucherCustomerID);
        }
      });

      const totalSpinsCount = spins?.length || 0;
      setTotalSpins(totalSpinsCount);

      const distributionData = Object.entries(distribution).map(([text, data]) => ({
        prize_text: text,
        prize_id: data.prize_id,
        count: data.count,
        percentage: totalSpinsCount > 0 ? (data.count / totalSpinsCount) * 100 : 0
      }));

      setSpinData(distributionData);
      setUniqueUsers(customerList.length);

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-32 bg-gray-200 rounded-lg"></div>
        <div className="h-64 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">Time Window:</label>
          <Select value={timeWindow} onValueChange={setTimeWindow}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1440">60 Days</SelectItem>
              <SelectItem value="2160">90 Days</SelectItem>
              <SelectItem value="2880">120 Days</SelectItem>
              <SelectItem value="8640">360 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Total Spins</p>
                <p className="text-2xl font-bold">{totalSpins}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Unique Users</p>
                <p className="text-2xl font-bold">{uniqueUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-muted-foreground">Avg Spins/User</p>
                <p className="text-2xl font-bold">
                  {uniqueUsers > 0 ? (totalSpins / uniqueUsers).toFixed(1) : '0'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Prize Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Prize Distribution (Last {Math.round(parseInt(timeWindow) / 24)} days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {spinData.map((item, index) => (
              <div key={item.prize_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="font-medium">{item.prize_text}</span>
                  <Badge variant="secondary">{item.count} wins</Badge>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min(100, item.percentage)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">
                    {item.percentage.toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default Analytics;